import { useEffect, useMemo, useState, useCallback } from "react";
import { Calendar as CalendarIcon, Clock, ChevronLeft, ChevronRight, X } from "lucide-react";
import { toast } from "sonner";
import { adminProductionCalendar, adminSetProductionDueDate } from "../../lib/api";

/**
 * Production scheduling calendar.
 *
 * Renders a month grid of paid orders by `production_due_date`, plus an
 * "Unscheduled" rail of paid orders without a date. Drag any unscheduled
 * card onto a day cell to assign it; drag a scheduled card to a new day to
 * reschedule; drag back to the rail to unschedule.
 *
 * Backend endpoints used:
 *   GET   /api/admin/production/calendar?month=YYYY-MM
 *   PATCH /api/admin/orders/{id}/production-due-date
 */
export default function ProductionTab({ pw }) {
  const [month, setMonth] = useState(() => isoMonth(new Date()));
  const [data, setData] = useState({ scheduled: [], backlog: [] });
  const [busy, setBusy] = useState(false);
  const [dragId, setDragId] = useState(null);

  const reload = useCallback(() => {
    adminProductionCalendar(pw, month)
      .then((r) => setData({ scheduled: r.scheduled || [], backlog: r.backlog || [] }))
      .catch(() => toast.error("Failed to load production calendar"));
  }, [pw, month]);

  useEffect(reload, [reload]);

  const move = async (orderId, dueDate) => {
    if (busy) return;
    setBusy(true);
    try {
      await adminSetProductionDueDate(orderId, dueDate, pw);
      toast.success(dueDate ? `Scheduled for ${dueDate}` : "Returned to backlog");
      reload();
    } catch {
      toast.error("Failed to update schedule");
    } finally {
      setBusy(false);
    }
  };

  const byDate = useMemo(() => {
    const m = new Map();
    for (const o of data.scheduled) {
      if (!o.production_due_date) continue;
      const list = m.get(o.production_due_date) || [];
      list.push(o);
      m.set(o.production_due_date, list);
    }
    return m;
  }, [data.scheduled]);

  const grid = useMemo(() => buildCalendar(month), [month]);

  const monthLabel = new Date(`${month}-01T00:00:00Z`).toLocaleString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });

  return (
    <div className="space-y-6" data-testid="production-tab">
      {/* Header / month nav */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <CalendarIcon size={20} className="text-[var(--tc-red)]" />
          <h2 className="font-display text-3xl uppercase">Production · {monthLabel}</h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setMonth(shiftMonth(month, -1))}
            data-testid="production-prev-month"
            className="p-2 border border-[#222] hover:border-[var(--tc-red)] hover:text-[var(--tc-red)]"
            aria-label="Previous month"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            onClick={() => setMonth(isoMonth(new Date()))}
            data-testid="production-today"
            className="font-mono text-[10px] tracking-widest px-3 py-2 border border-[#222] hover:border-[var(--tc-red)] hover:text-[var(--tc-red)]"
          >
            TODAY
          </button>
          <button
            onClick={() => setMonth(shiftMonth(month, +1))}
            data-testid="production-next-month"
            className="p-2 border border-[#222] hover:border-[var(--tc-red)] hover:text-[var(--tc-red)]"
            aria-label="Next month"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_280px] gap-6">
        {/* Month grid */}
        <div>
          <div className="grid grid-cols-7 gap-px bg-[#1a1a1a] border border-[#1a1a1a] mb-px">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
              <div key={d} className="bg-[#050505] p-2 font-mono text-[10px] tracking-widest text-zinc-500 text-center">
                {d.toUpperCase()}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-px bg-[#1a1a1a] border border-[#1a1a1a]">
            {grid.map((d) => {
              const orders = (d.iso && byDate.get(d.iso)) || [];
              const isToday = d.iso === isoDate(new Date());
              const isPast = d.iso && d.iso < isoDate(new Date()) && !isToday;
              return (
                <div
                  key={d.key}
                  data-testid={d.iso ? `prod-day-${d.iso}` : `prod-day-empty-${d.key}`}
                  onDragOver={(e) => { if (d.iso) e.preventDefault(); }}
                  onDrop={(e) => {
                    if (!d.iso || !dragId) return;
                    e.preventDefault();
                    move(dragId, d.iso);
                    setDragId(null);
                  }}
                  className={`min-h-[110px] p-2 transition-colors ${
                    !d.iso ? "bg-[#0a0a0a]" : isToday ? "bg-[#1a0a0a]" : "bg-black hover:bg-[#0d0d0d]"
                  }`}
                >
                  {d.iso && (
                    <>
                      <div className={`font-mono text-[10px] tracking-widest mb-2 ${
                        isToday ? "text-[var(--tc-red)] font-bold" : isPast ? "text-zinc-600" : "text-zinc-400"
                      }`}>
                        {d.day}{isToday ? " · TODAY" : ""}
                      </div>
                      <div className="space-y-1">
                        {orders.slice(0, 3).map((o) => (
                          <OrderChip
                            key={o.id}
                            order={o}
                            onDragStart={() => setDragId(o.id)}
                            onDragEnd={() => setDragId(null)}
                            onUnschedule={() => move(o.id, null)}
                          />
                        ))}
                        {orders.length > 3 && (
                          <div className="font-mono text-[9px] text-zinc-500">+ {orders.length - 3} more</div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Backlog rail */}
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            if (!dragId) return;
            e.preventDefault();
            move(dragId, null);
            setDragId(null);
          }}
          className="bg-[#050505] border border-[#222] p-4 max-h-[640px] overflow-y-auto"
          data-testid="production-backlog"
        >
          <div className="flex items-center gap-2 mb-3">
            <Clock size={14} className="text-[var(--tc-red)]" />
            <div className="font-mono text-[11px] tracking-widest">UNSCHEDULED · {data.backlog.length}</div>
          </div>
          <p className="font-mono text-[9px] tracking-widest text-zinc-500 mb-3">
            DRAG ONTO A DAY TO SCHEDULE
          </p>
          {data.backlog.length === 0 && (
            <div className="text-zinc-500 text-xs font-mono py-4">No unscheduled paid orders 🎉</div>
          )}
          <div className="space-y-2">
            {data.backlog.map((o) => (
              <OrderChip
                key={o.id}
                order={o}
                onDragStart={() => setDragId(o.id)}
                onDragEnd={() => setDragId(null)}
                inBacklog
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function OrderChip({ order, onDragStart, onDragEnd, onUnschedule, inBacklog = false }) {
  const itemCount = (order.items || []).reduce((s, it) => s + (it.quantity || 0), 0);
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      data-testid={`prod-order-${order.id}`}
      className={`group bg-black border border-[#222] hover:border-[var(--tc-red)] p-2 cursor-grab active:cursor-grabbing transition-colors ${inBacklog ? "" : ""}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[10px] text-zinc-300 truncate">{order.order_number}</div>
          <div className="font-mono text-[9px] text-zinc-500 truncate">
            {(order.customer?.name) || "—"} · {itemCount} pc{itemCount === 1 ? "" : "s"}
          </div>
        </div>
        {!inBacklog && onUnschedule && (
          <button
            onClick={(e) => { e.stopPropagation(); onUnschedule(); }}
            className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-[var(--tc-red)] transition-opacity"
            data-testid={`prod-unschedule-${order.id}`}
            aria-label="Unschedule"
          >
            <X size={12} />
          </button>
        )}
      </div>
      <div className="mt-1 flex items-center justify-between">
        <span className="font-mono text-[9px] text-[var(--tc-red)]">${(order.total || 0).toFixed(0)}</span>
        <span className="font-mono text-[9px] text-zinc-600">{(order.fulfillment_status || "paid").replace("_", " ")}</span>
      </div>
    </div>
  );
}

// ---- helpers ----
function isoMonth(d) {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}
function isoDate(d) {
  return d.toISOString().slice(0, 10);
}
function shiftMonth(monthIso, delta) {
  const [y, m] = monthIso.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return isoMonth(d);
}
function buildCalendar(monthIso) {
  // Returns 6×7 = 42 cells with `{ iso, day, key }` for the month, padded
  // with empties on either side. Starts the week on Monday.
  const [y, m] = monthIso.split("-").map(Number);
  const firstOfMonth = new Date(Date.UTC(y, m - 1, 1));
  const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const jsDow = firstOfMonth.getUTCDay(); // 0 = Sun
  const padBefore = (jsDow + 6) % 7;       // Monday-start
  const cells = [];
  for (let i = 0; i < padBefore; i++) cells.push({ iso: null, day: null, key: `pad-${i}` });
  for (let day = 1; day <= daysInMonth; day++) {
    const iso = `${y}-${String(m).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    cells.push({ iso, day, key: iso });
  }
  while (cells.length % 7 !== 0) cells.push({ iso: null, day: null, key: `tail-${cells.length}` });
  // Keep grid at most 6 weeks
  return cells.slice(0, 42);
}
